/**
 * Fechas de calendario sin zona horaria.
 *
 * Todo lo financiero de PayBae ocurre en fechas de calendario, no en instantes:
 * una fecha de corte, un vencimiento y un día de nómina son el mismo día sin
 * importar la hora ni el huso desde el que se consulten. Usar `Date` de JS aquí
 * es la causa clásica del error de un día, porque `Date` es un instante y se
 * desplaza al cruzar husos u horario de verano.
 *
 * Por eso la representación canónica es la cadena `YYYY-MM-DD`:
 *   - se serializa sola en JSON,
 *   - se ordena lexicográficamente igual que cronológicamente,
 *   - es exactamente lo que devuelve una columna `DATE` de Postgres,
 *   - y no tiene huso que se pueda desplazar.
 *
 * La aritmética se hace sobre días-desde-época (enteros), así que sumar días
 * nunca puede producir una hora inválida. La zona horaria solo aparece en
 * `todayIn`, que es la única frontera con el tiempo real.
 */

/** Fecha de calendario en formato `YYYY-MM-DD`. */
export type PlainDate = string & { readonly __brand: 'PlainDate' }

export interface DateParts {
  /** Año proléptico gregoriano. */
  year: number
  /** Mes 1–12. */
  month: number
  /** Día 1–31. */
  day: number
}

/**
 * Domingo = 0 … Sábado = 6. Coincide con `Date.prototype.getDay`.
 *
 * Objeto constante en vez de `enum` porque esbuild —el compilador que usa
 * Vite— no puede resolver un `const enum` que cruza módulos, y un `enum`
 * normal emite código que no se puede eliminar por tree-shaking.
 */
export const Weekday = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const

export type Weekday = (typeof Weekday)[keyof typeof Weekday]

const DAYS_0000_TO_1970 = 719_528
const DAYS_PER_CYCLE = 146_097
const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function daysInMonth(year: number, month: number): number {
  switch (month) {
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      return 31
    case 4:
    case 6:
    case 9:
    case 11:
      return 30
    case 2:
      return isLeapYear(year) ? 29 : 28
    default:
      throw new RangeError(`Mes fuera de rango: ${month}`)
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function pad4(n: number): string {
  return String(n).padStart(4, '0')
}

/** Construye una fecha validando que el día exista realmente en ese mes. */
export function plainDate(year: number, month: number, day: number): PlainDate {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new RangeError(`Fecha no entera: ${year}-${month}-${day}`)
  }
  if (month < 1 || month > 12) {
    throw new RangeError(`Mes fuera de rango: ${month}`)
  }
  const max = daysInMonth(year, month)
  if (day < 1 || day > max) {
    throw new RangeError(`El día ${day} no existe en ${pad4(year)}-${pad2(month)}`)
  }
  return `${pad4(year)}-${pad2(month)}-${pad2(day)}` as PlainDate
}

/** Alias corto para literales dentro de pruebas y semillas. */
export function pd(iso: string): PlainDate {
  return parsePlainDate(iso)
}

export function isPlainDate(value: unknown): value is PlainDate {
  if (typeof value !== 'string') return false
  const m = ISO_PATTERN.exec(value)
  if (!m) return false
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12) return false
  return day >= 1 && day <= daysInMonth(year, month)
}

export function parsePlainDate(iso: string): PlainDate {
  const m = ISO_PATTERN.exec(iso)
  if (!m) {
    throw new RangeError(`Se esperaba YYYY-MM-DD y se recibió: ${iso}`)
  }
  return plainDate(Number(m[1]), Number(m[2]), Number(m[3]))
}

export function toParts(date: PlainDate): DateParts {
  return {
    year: Number(date.slice(0, 4)),
    month: Number(date.slice(5, 7)),
    day: Number(date.slice(8, 10)),
  }
}

export function getYear(date: PlainDate): number {
  return Number(date.slice(0, 4))
}

export function getMonth(date: PlainDate): number {
  return Number(date.slice(5, 7))
}

export function getDayOfMonth(date: PlainDate): number {
  return Number(date.slice(8, 10))
}

/**
 * Días transcurridos desde 1970-01-01. Misma definición que
 * `java.time.LocalDate.toEpochDay`, elegida por estar ampliamente verificada.
 */
export function toEpochDay(date: PlainDate): number {
  const { year, month, day } = toParts(date)
  let total = 365 * year
  if (year >= 0) {
    total += Math.floor((year + 3) / 4) - Math.floor((year + 99) / 100) + Math.floor((year + 399) / 400)
  } else {
    total -= Math.floor(year / -4) - Math.floor(year / -100) + Math.floor(year / -400)
  }
  total += Math.floor((367 * month - 362) / 12)
  total += day - 1
  if (month > 2) {
    total -= 1
    if (!isLeapYear(year)) total -= 1
  }
  return total - DAYS_0000_TO_1970
}

/** Inversa de `toEpochDay`. */
export function fromEpochDay(epochDay: number): PlainDate {
  let zeroDay = epochDay + DAYS_0000_TO_1970
  // Se corre el origen a 0000-03-01 para que el día bisiesto quede al final
  // del ciclo de cuatro años y la aritmética no tenga casos especiales.
  zeroDay -= 60
  let adjust = 0
  if (zeroDay < 0) {
    const adjustCycles = Math.floor((zeroDay + 1) / DAYS_PER_CYCLE) - 1
    adjust = adjustCycles * 400
    zeroDay += -adjustCycles * DAYS_PER_CYCLE
  }
  let yearEst = Math.floor((400 * zeroDay + 591) / DAYS_PER_CYCLE)
  let doyEst =
    zeroDay -
    (365 * yearEst + Math.floor(yearEst / 4) - Math.floor(yearEst / 100) + Math.floor(yearEst / 400))
  if (doyEst < 0) {
    yearEst -= 1
    doyEst =
      zeroDay -
      (365 * yearEst + Math.floor(yearEst / 4) - Math.floor(yearEst / 100) + Math.floor(yearEst / 400))
  }
  yearEst += adjust

  const marchDoy0 = doyEst
  const marchMonth0 = Math.floor((marchDoy0 * 5 + 2) / 153)
  const month = ((marchMonth0 + 2) % 12) + 1
  const day = marchDoy0 - Math.floor((marchMonth0 * 306 + 5) / 10) + 1
  const year = yearEst + Math.floor(marchMonth0 / 10)

  return plainDate(year, month, day)
}

export function addDays(date: PlainDate, days: number): PlainDate {
  if (days === 0) return date
  return fromEpochDay(toEpochDay(date) + days)
}

export function subDays(date: PlainDate, days: number): PlainDate {
  return addDays(date, -days)
}

/**
 * Suma meses recortando el día al último válido del mes destino.
 * 31 de enero + 1 mes → 28/29 de febrero, que es la convención que usan los
 * bancos para recorrer ciclos de corte.
 */
export function addMonths(date: PlainDate, months: number): PlainDate {
  if (months === 0) return date
  const { year, month, day } = toParts(date)
  const totalMonths = year * 12 + (month - 1) + months
  const newYear = Math.floor(totalMonths / 12)
  const newMonth = (((totalMonths % 12) + 12) % 12) + 1
  const newDay = Math.min(day, daysInMonth(newYear, newMonth))
  return plainDate(newYear, newMonth, newDay)
}

export function dayOfWeek(date: PlainDate): Weekday {
  // El día de época 0 (1970-01-01) fue jueves.
  const dow = (((toEpochDay(date) + 4) % 7) + 7) % 7
  return dow as Weekday
}

export function isWeekend(date: PlainDate): boolean {
  const dow = dayOfWeek(date)
  return dow === Weekday.Saturday || dow === Weekday.Sunday
}

export function startOfMonth(date: PlainDate): PlainDate {
  const { year, month } = toParts(date)
  return plainDate(year, month, 1)
}

export function endOfMonth(date: PlainDate): PlainDate {
  const { year, month } = toParts(date)
  return plainDate(year, month, daysInMonth(year, month))
}

/**
 * Fija un día del mes, recortándolo si no existe.
 * `dayOfMonth = 0` significa «último día del mes», que es como se captura la
 * segunda quincena de una nómina.
 */
export function withDayOfMonth(date: PlainDate, dayOfMonth: number): PlainDate {
  const { year, month } = toParts(date)
  const max = daysInMonth(year, month)
  const day = dayOfMonth === 0 ? max : Math.min(dayOfMonth, max)
  return plainDate(year, month, day)
}

/**
 * N-ésimo día de semana de un mes. `nth = -1` devuelve el último.
 * Sirve para festivos como «tercer lunes de marzo».
 */
export function nthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: Weekday,
  nth: number,
): PlainDate {
  if (nth === 0) throw new RangeError('nth debe ser distinto de cero')

  if (nth > 0) {
    const first = plainDate(year, month, 1)
    const offset = (weekday - dayOfWeek(first) + 7) % 7
    const day = 1 + offset + (nth - 1) * 7
    if (day > daysInMonth(year, month)) {
      throw new RangeError(`No existe el ${nth}º día ${weekday} en ${year}-${month}`)
    }
    return plainDate(year, month, day)
  }

  const last = endOfMonth(plainDate(year, month, 1))
  const offset = (dayOfWeek(last) - weekday + 7) % 7
  const day = getDayOfMonth(last) - offset + (nth + 1) * 7
  if (day < 1) {
    throw new RangeError(`No existe el ${nth}º día ${weekday} en ${year}-${month}`)
  }
  return plainDate(year, month, day)
}

export function compareDates(a: PlainDate, b: PlainDate): -1 | 0 | 1 {
  // El formato ISO acolchado hace que el orden de cadena sea el cronológico.
  return a < b ? -1 : a > b ? 1 : 0
}

export const isBefore = (a: PlainDate, b: PlainDate): boolean => a < b
export const isAfter = (a: PlainDate, b: PlainDate): boolean => a > b
export const isSameDay = (a: PlainDate, b: PlainDate): boolean => a === b
export const isOnOrBefore = (a: PlainDate, b: PlainDate): boolean => a <= b
export const isOnOrAfter = (a: PlainDate, b: PlainDate): boolean => a >= b

/** Inclusivo en ambos extremos. */
export function isWithin(date: PlainDate, start: PlainDate, end: PlainDate): boolean {
  return date >= start && date <= end
}

export function minDate(...dates: PlainDate[]): PlainDate {
  if (dates.length === 0) throw new RangeError('minDate necesita al menos una fecha')
  return dates.reduce((a, b) => (a <= b ? a : b))
}

export function maxDate(...dates: PlainDate[]): PlainDate {
  if (dates.length === 0) throw new RangeError('maxDate necesita al menos una fecha')
  return dates.reduce((a, b) => (a >= b ? a : b))
}

/** `later - earlier`, en días. Negativo si `later` es anterior. */
export function differenceInDays(later: PlainDate, earlier: PlainDate): number {
  return toEpochDay(later) - toEpochDay(earlier)
}

/** Rango inclusivo, útil para proyectar el flujo de un mes día por día. */
export function eachDayInRange(start: PlainDate, end: PlainDate): PlainDate[] {
  if (start > end) return []
  const out: PlainDate[] = []
  const last = toEpochDay(end)
  for (let d = toEpochDay(start); d <= last; d++) {
    out.push(fromEpochDay(d))
  }
  return out
}

/**
 * Hoy según la zona horaria del hogar. Única frontera entre el calendario y el
 * reloj: `en-CA` produce `YYYY-MM-DD`, así que no hay reformateo manual que
 * pueda introducir un desfase.
 */
export function todayIn(timeZone: string, now: Date = new Date()): PlainDate {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
  return parsePlainDate(formatted)
}

/**
 * Instante UTC correspondiente a una hora local de la zona indicada.
 * Se usa para programar notificaciones: «las 9:00 en Ciudad de México».
 * Resuelve el desplazamiento por aproximación sucesiva, que es exacto incluso
 * en los cambios de horario de verano.
 */
export function zonedTimeToUtc(
  date: PlainDate,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const { year, month, day } = toParts(date)
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0)
  const offset1 = timeZoneOffsetMs(guess, timeZone)
  const candidate = guess - offset1
  // Un segundo pase absorbe los saltos de horario de verano.
  const offset2 = timeZoneOffsetMs(candidate, timeZone)
  return new Date(guess - offset2)
}

function timeZoneOffsetMs(utcMs: number, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = dtf.formatToParts(new Date(utcMs))
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0')
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  )
  return asUtc - utcMs
}
